import type { CSSProperties } from 'react';
import { ViewMode } from '../../domain/types';
import type { BrandType } from '../../ui/common/BrandIcon';
import type { CapabilityId } from '../../domain/capabilityNavigation';
import type { ConnectableSource } from '../../domain/providerIds';
import { SOURCE_PROVIDER_ORDER, SOURCE_PROVIDERS } from '../../domain/providerRegistry';
import { SIDEBAR_COLLAPSED_PX, SIDEBAR_EXPANDED_PX } from './sidebarConstants';

export interface SidebarCounts {
  radar: number;
  notifications: number;
  jira: number;
  slack: number;
  github: number;
  gmail: number;
  agents: number;
  reminders: number;
  notes: number;
  wiki: number;
}

export interface SidebarNavSource {
  id: ViewMode;
  capability: CapabilityId;
  label: string;
  providerLabel: string;
  count: number;
  brand: BrandType;
  shortcut: string;
}

export function resolveShowSidebarLabels(isCompact: boolean, isSidebarExpanded: boolean): boolean {
  return isCompact || isSidebarExpanded;
}

export function resolveSidebarWidthPx(isCompact: boolean, isSidebarExpanded: boolean): number {
  if (isCompact) return SIDEBAR_EXPANDED_PX;
  if (isSidebarExpanded) return SIDEBAR_EXPANDED_PX;
  return SIDEBAR_COLLAPSED_PX;
}

/**
 * Builds the sidebar's provider entries from `SOURCE_PROVIDERS` (see
 * `providerRegistry.ts`) followed by the fixed Knowledge Wiki entry. Adding a
 * connectable source means adding it to `SOURCE_PROVIDER_ORDER` — no edit
 * here. `_loadingSources` is accepted for API stability but not currently
 * consulted (kept from the pre-registry signature).
 */
export function buildSidebarNavSources(
  counts: SidebarCounts,
  _loadingSources: Partial<Record<ConnectableSource, boolean>> = {},
): SidebarNavSource[] {
  const providerEntries: SidebarNavSource[] = SOURCE_PROVIDER_ORDER.map((id) => {
    const def = SOURCE_PROVIDERS[id];
    return {
      id: def.view,
      capability: def.capability,
      label: def.navLabel,
      providerLabel: def.label,
      count: counts[id],
      brand: def.brand,
      shortcut: def.shortcut,
    };
  });
  return [
    ...providerEntries,
    {
      id: 'wiki',
      capability: 'workspace',
      label: 'Knowledge Wiki',
      providerLabel: 'Workspace knowledge',
      count: counts.wiki,
      brand: 'wiki',
      shortcut: '6',
    },
  ];
}

export function resolveWorkspaceLabel(displayName?: string, email?: string): string {
  if (displayName) return displayName;
  if (email) {
    const local = email.split('@')[0];
    if (local) return local;
  }
  return 'workspace';
}

export function resolveAuthLabel(displayName?: string | null, email?: string | null): string {
  if (displayName) return displayName;
  if (email) return email;
  return 'Signed in';
}

export function resolveAvatarInitials(email?: string | null): string {
  const raw = email ?? 'JU';
  return raw.slice(0, 2).toUpperCase();
}

export function sidebarShellStyle(
  sidebarWidthPx: number,
  showSidebarLabels: boolean,
  isCompact: boolean,
): CSSProperties {
  return {
    width: sidebarWidthPx,
    minWidth: sidebarWidthPx,
    flex: `0 0 ${sidebarWidthPx}px`,
    padding: showSidebarLabels ? '18px 14px' : '18px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    boxSizing: 'border-box',
    background: 'var(--z-surface)',
    borderRight: 'none',
    overflowX: 'hidden',
    overflowY: 'auto',
    height: '100vh',
    transition: isCompact
      ? undefined
      : 'width 0.22s cubic-bezier(0.16, 1, 0.3, 1), flex 0.22s cubic-bezier(0.16, 1, 0.3, 1), min-width 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
    zIndex: isCompact ? undefined : 20,
  };
}
